/**
 * 前台 Blade：導覽列捲動、第一／二屆首頁主視覺揭幕與視差。
 */

const NAV_STICKY_GAP_PX = 72;

function shouldShowStickyNav(nav: HTMLElement): boolean {
    const inverted = nav.dataset.inverted === 'true';
    const hero = document.getElementById('home-keyvisual');
    const stickyThresholdRaw = nav.dataset.stickyThresholdPx ?? '';

    if (hero) {
        return hero.getBoundingClientRect().bottom <= NAV_STICKY_GAP_PX;
    }

    const visualBanner = document.querySelector<HTMLElement>('.visual-banner');
    if (visualBanner && nav.dataset.deferLogo === 'true') {
        return visualBanner.getBoundingClientRect().bottom <= NAV_STICKY_GAP_PX;
    }

    const stickyThreshold = Number.parseInt(stickyThresholdRaw, 10);
    if (!Number.isNaN(stickyThreshold) && stickyThreshold > 0) {
        return window.scrollY > stickyThreshold;
    }

    if (inverted) {
        return window.scrollY > 600;
    }

    // 一般白底導覽（無主視覺的內頁，例如第二屆 post detail、隱私權）：捲動後 fixed bar
    return window.scrollY > 32;
}

function initSiteNavbar(): void {
    document.querySelectorAll<HTMLElement>('[data-site-navbar]').forEach((nav) => {
        const inverted = nav.dataset.inverted === 'true';
        const deferLogo = nav.dataset.deferLogo === 'true';

        const deferLogos = nav.querySelectorAll<HTMLImageElement>('img.site-navbar__logo--defer');

        const swapLogos = nav.querySelectorAll<HTMLImageElement>(
            '.site-navbar__logo[data-logo-dark][data-logo-light]:not(.site-navbar__logo--defer)',
        );

        const syncDeferLogos = (show: boolean): void => {
            deferLogos.forEach((logo) => {
                if (show) {
                    logo.classList.remove('hidden');
                    logo.removeAttribute('aria-hidden');
                } else {
                    logo.classList.add('hidden');
                    logo.setAttribute('aria-hidden', 'true');
                }
            });
        };

        const syncSwapLogos = (useDark: boolean): void => {
            swapLogos.forEach((logo) => {
                const dark = logo.dataset.logoDark;
                const light = logo.dataset.logoLight;
                if (dark && light) {
                    logo.setAttribute('src', useDark ? dark : light);
                }
            });
        };

        const onScrollOrResize = (): void => {
            const sticky = shouldShowStickyNav(nav);
            nav.classList.toggle('site-navbar--scrolled', sticky);

            if (deferLogo) {
                syncDeferLogos(sticky);
            }
            // 手機橫式 logo（與桌機非 defer 區塊）捲動後一律切換深／淺色檔
            if (inverted) {
                syncSwapLogos(sticky);
            }
        };

        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize, { passive: true });
        onScrollOrResize();
    });
}

/** 英文首頁「中文」：對齊 home-en.tsx FadeInDiv 揭幕後 opacity / translate */
function syncEnglishHomeZhNav(visible: boolean): void {
    document.querySelectorAll<HTMLElement>('.home-en-zh-nav').forEach((nav) => {
        nav.classList.toggle('is-visible', visible);
    });
}

/** 第一屆首頁 #home-keyvisual.home-keyvisual--first：對齊 home.tsx 揭幕延遲與捲動視差 */
function initHomeKeyvisualFirst(): void {
    const root = document.querySelector<HTMLElement>('#home-keyvisual.home-keyvisual--first');
    if (!root) {
        return;
    }

    const hasEnZhNav = document.querySelector('.home-en-zh-nav') !== null;

    const bg = root.querySelector<HTMLElement>('.home-kv__bg');
    const curtain = root.querySelector<HTMLElement>('.home-kv__curtain');
    const flare = root.querySelector<HTMLElement>('.home-kv__flare');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let kvParallaxReady = reduced;

    const syncParallax = (): void => {
        if (!root.classList.contains('home-kv--revealed')) {
            return;
        }
        const y = window.scrollY;
        if (bg) {
            bg.style.top = `${y * 0.33}px`;
        }
        if (!kvParallaxReady) {
            return;
        }
        if (curtain) {
            curtain.style.transform = `translate3d(0, ${y * 0.41}px, 0) scale(1)`;
            curtain.style.transitionDuration = y > 1 ? '2ms' : '';
        }
        if (flare) {
            flare.style.transform = `translate3d(0, ${y * 0.5}px, 0)`;
        }
    };

    const reveal = (): void => {
        root.classList.add('home-kv--revealed');
        if (hasEnZhNav) {
            syncEnglishHomeZhNav(true);
        }
        if (!reduced) {
            window.setTimeout(() => {
                kvParallaxReady = true;
                syncParallax();
            }, 2100);
        } else {
            kvParallaxReady = true;
        }
    };

    if (reduced) {
        reveal();
    } else {
        window.setTimeout(reveal, 1200);
    }

    window.addEventListener('scroll', syncParallax, { passive: true });
    window.addEventListener('resize', syncParallax, { passive: true });
    syncParallax();
}

/** 第二屆首頁 #home-keyvisual.home-keyvisual--second：對齊 banner-visual.tsx */
function initHomeKeyvisualSecond(): void {
    const root = document.querySelector<HTMLElement>('#home-keyvisual.home-keyvisual--second');
    if (!root) {
        return;
    }

    const hasEnZhNav = document.querySelector('.home-en-zh-nav') !== null;

    const bg = root.querySelector<HTMLElement>('.home-kv2__bg');
    const curtain = root.querySelector<HTMLElement>('.home-kv2__curtain');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let kvParallaxReady = reduced;

    const syncParallax = (): void => {
        if (!root.classList.contains('home-kv--revealed')) {
            return;
        }
        const y = window.scrollY;
        if (curtain) {
            curtain.style.top = `${y * 0.33}px`;
        }
        if (!kvParallaxReady) {
            return;
        }
        if (bg) {
            bg.style.transform = `translate3d(0, ${y * 0.41}px, 0) scale(1)`;
            bg.style.transitionDuration = y > 1 ? '2ms' : '';
        }
    };

    const reveal = (): void => {
        root.classList.add('home-kv--revealed');
        if (hasEnZhNav) {
            syncEnglishHomeZhNav(true);
        }
        if (!reduced) {
            window.setTimeout(() => {
                kvParallaxReady = true;
                syncParallax();
            }, 2100);
        } else {
            kvParallaxReady = true;
        }
    };

    if (reduced) {
        reveal();
    } else {
        window.setTimeout(reveal, 1200);
    }

    window.addEventListener('scroll', syncParallax, { passive: true });
    window.addEventListener('resize', syncParallax, { passive: true });
    syncParallax();
}

/** 評審團頁：開關介紹彈層時鎖定背景捲動並維持原捲動位置（避免開／關後頁面上跳） */
function initJudgeModals(): void {
    const root = document.querySelector('.judge-modals-root');
    if (!root) {
        return;
    }

    const toggles = root.querySelectorAll<HTMLInputElement>('.judge-modal-toggle');
    let savedScrollY = 0;
    let scrollLocked = false;

    const preserveScroll = (y: number): void => {
        window.scrollTo(0, y);
        requestAnimationFrame(() => {
            window.scrollTo(0, y);
        });
    };

    const lockScroll = (y: number): void => {
        if (scrollLocked) {
            return;
        }
        savedScrollY = y;
        scrollLocked = true;
        document.documentElement.classList.add('judge-modal-open');
        document.body.classList.add('judge-modal-open');
        preserveScroll(savedScrollY);
    };

    const unlockScroll = (): void => {
        if (!scrollLocked) {
            return;
        }
        const y = savedScrollY;
        scrollLocked = false;
        document.documentElement.classList.remove('judge-modal-open');
        document.body.classList.remove('judge-modal-open');
        preserveScroll(y);
    };

    const setModalOpen = (openId: string | null, scrollY: number): void => {
        toggles.forEach((t) => {
            t.checked = openId !== null && t.id === openId;
        });

        if (openId) {
            lockScroll(scrollY);
        } else {
            unlockScroll();
        }
    };

    document.querySelectorAll<HTMLLabelElement>('label.judge-modal-trigger[for]').forEach((label) => {
        const id = label.getAttribute('for');
        if (!id) {
            return;
        }

        const toggle = root.querySelector<HTMLInputElement>(`#${CSS.escape(id)}`);
        if (!toggle?.classList.contains('judge-modal-toggle')) {
            return;
        }

        let pointerScrollY = window.scrollY;

        label.addEventListener('pointerdown', (e) => {
            pointerScrollY = window.scrollY;
            e.preventDefault();
        });

        label.addEventListener('click', (e) => {
            e.preventDefault();
            const willOpen = !toggle.checked;
            setModalOpen(willOpen ? id : null, pointerScrollY);
        });
    });

}

/** 第一屆關於頁主視覺：捲動時 banner 圖切換與入場動畫（對齊 about-banner.tsx InView） */
function initAboutBanner(): void {
    const banner = document.querySelector<HTMLElement>('[data-about-banner]');
    if (!banner) {
        return;
    }

    const syncFromEntry = (entry: IntersectionObserverEntry): void => {
        const inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        const showStart = !(inView || entry.intersectionRatio > 0.8);
        banner.classList.toggle('about-banner__start', showStart);
    };

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach(syncFromEntry);
        },
        { threshold: [0, 0.5, 0.8, 1] },
    );

    observer.observe(banner);
}

/** 第一屆關於頁獎盃：捲動位置對應影格（停止捲動即停，不逐格追趕） */
function initAboutTrophy(): void {
    const frame = document.getElementById('about-trophy-frame') as HTMLImageElement | null;
    const contentEl = document.getElementById('about-content');
    if (!frame || !contentEl) {
        return;
    }

    let trophyStartY = 0;
    let trophyEndY = 0;
    let renderedIndex = 0;
    let pendingRaf: number | null = null;

    const padFrame = (index: number): string => String(index).padStart(3, '0');

    const updateBounds = (): void => {
        const scrollY = window.scrollY;
        const rect = contentEl.getBoundingClientRect();
        // 拉長捲動區間，讓換格較慢、較順
        trophyStartY = rect.top + scrollY - window.innerHeight * 2;
        trophyEndY = trophyStartY + contentEl.clientHeight + window.innerHeight * 1.6;
    };

    const renderFromScroll = (): void => {
        pendingRaf = null;
        updateBounds();

        const range = trophyEndY - trophyStartY;
        if (range <= 0) {
            return;
        }

        const scrollPercentage = Math.min(
            Math.max((window.scrollY - trophyStartY) / range, 0),
            1,
        );
        const index = Math.min(Math.max(Math.floor(scrollPercentage * 298) + 1, 1), 299);

        if (index === renderedIndex) {
            return;
        }

        renderedIndex = index;
        frame.src = `/images/about/trophy/${padFrame(index)}.jpg`;
    };

    const scheduleRender = (): void => {
        if (pendingRaf !== null) {
            return;
        }
        pendingRaf = requestAnimationFrame(renderFromScroll);
    };

    renderFromScroll();
    window.addEventListener('scroll', scheduleRender, { passive: true });
    window.addEventListener('resize', scheduleRender);
}

type SiteVideoEmbed = { kind: 'file'; src: string } | { kind: 'iframe'; src: string };

function resolveSiteVideoEmbed(url: string): SiteVideoEmbed | null {
    const trimmed = url.trim();
    if (!trimmed) {
        return null;
    }

    try {
        const parsed = new URL(trimmed, window.location.origin);
        const host = parsed.hostname.replace(/^www\./, '');

        if (host === 'youtu.be') {
            const id = parsed.pathname.replace(/^\//, '').split('/')[0];
            return id ? { kind: 'iframe', src: `https://www.youtube.com/embed/${id}?autoplay=1` } : null;
        }

        if (host === 'youtube.com' || host === 'm.youtube.com') {
            const shorts = parsed.pathname.match(/^\/shorts\/([^/]+)/);
            if (shorts?.[1]) {
                return { kind: 'iframe', src: `https://www.youtube.com/embed/${shorts[1]}?autoplay=1` };
            }
            const id = parsed.searchParams.get('v');
            if (id) {
                return { kind: 'iframe', src: `https://www.youtube.com/embed/${id}?autoplay=1` };
            }
            const embed = parsed.pathname.match(/^\/embed\/([^/]+)/);
            if (embed?.[1]) {
                return { kind: 'iframe', src: `https://www.youtube.com/embed/${embed[1]}?autoplay=1` };
            }
        }

        if (host === 'vimeo.com') {
            const id = parsed.pathname.replace(/^\//, '').split('/')[0];
            return id ? { kind: 'iframe', src: `https://player.vimeo.com/video/${id}?autoplay=1` } : null;
        }
    } catch {
        // fall through to direct file URL
    }

    return { kind: 'file', src: trimmed };
}

/** 節目／藝術家頁：對齊 ShowVideoModal（show-video.tsx），並支援 YouTube / Vimeo */
function initSiteShowVideoModal(): void {
    const modal = document.getElementById('site-show-video-modal');
    const player = modal?.querySelector<HTMLVideoElement>('[data-site-video-player]');
    const iframe = modal?.querySelector<HTMLIFrameElement>('[data-site-video-iframe]');
    const openBtns = document.querySelectorAll<HTMLButtonElement>('[data-site-video-open]');
    if (!modal || !player || !iframe || openBtns.length === 0) {
        return;
    }

    if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
    }

    let savedScrollY = 0;

    const lockScroll = (): void => {
        savedScrollY = window.scrollY;
        document.documentElement.classList.add('site-video-modal-open');
        document.body.classList.add('site-video-modal-open');
        window.scrollTo(0, savedScrollY);
    };

    const unlockScroll = (): void => {
        const y = savedScrollY;
        document.documentElement.classList.remove('site-video-modal-open');
        document.body.classList.remove('site-video-modal-open');
        window.scrollTo(0, y);
    };

    const hidePlayers = (): void => {
        player.pause();
        player.removeAttribute('src');
        player.load();
        player.hidden = true;
        iframe.src = '';
        iframe.hidden = true;
    };

    const closeModal = (): void => {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        modal.hidden = true;
        hidePlayers();
        unlockScroll();
    };

    const openModal = (videoUrl: string): void => {
        const embed = resolveSiteVideoEmbed(videoUrl);
        if (!embed) {
            return;
        }

        hidePlayers();

        if (embed.kind === 'iframe') {
            iframe.src = embed.src;
            iframe.hidden = false;
        } else {
            player.src = embed.src;
            player.hidden = false;
            void player.play().catch(() => {});
        }

        modal.hidden = false;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        lockScroll();
    };

    openBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const videoUrl = btn.dataset.videoUrl?.trim() ?? '';
            if (videoUrl) {
                openModal(videoUrl);
            }
        });
    });

    modal.querySelectorAll<HTMLElement>('[data-site-video-close]').forEach((el) => {
        el.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (e) => {
        if (e.target === modal.querySelector('.site-show-video-modal__backdrop')) {
            closeModal();
        }
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
            closeModal();
        }
    });
}

function bootSiteUi(): void {
    initSiteNavbar();
    initHomeKeyvisualFirst();
    initHomeKeyvisualSecond();
    initJudgeModals();
    initSiteShowVideoModal();
    initAboutBanner();
    initAboutTrophy();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootSiteUi);
} else {
    bootSiteUi();
}
